import React from 'react'

import {
  AnalyticsClientProvider,
  ApolloErrorModalProvider,
  PromptPortalProvider,
  RemoteStringsProvider,
} from '@acorns/web-utils'
import { ReactSDKClient } from '@optimizely/react-sdk'
import { branch, compose, lifecycle, renderComponent, withState } from 'recompose'
import { ThemeProvider } from 'styled-components'

import CenteredSpinner from 'components/CenteredSpinner'
import { ErrorBoundary } from 'components/ErrorBoundary'
import { ApolloErrorHandlingProvider, ApolloProvider } from 'utils/Apollo'
import { BackendProvider } from 'utils/backend'
import Environment, { EnvironmentProvider } from 'utils/environment'
import { Provider as PromptProvider } from 'utils/prompts'

import Router from './router'
import withLogVersion from './utils/with-log-version'

type OuterProps = {
  appId: string
  backend: any
  environment: Environment
  theme: any
  optimizely?: ReactSDKClient
}

type InnerProps = {
  isReady: boolean
  setIsReady: (ready: boolean) => void
}

type Props = InnerProps & OuterProps

const enhance = compose<Props, OuterProps>(
  withLogVersion,
  withState('isReady', 'setIsReady', false),
  lifecycle<Props, {}>({
    componentDidMount() {
      if (!this.props.optimizely) {
        this.props.setIsReady(true)
      }
      this.props.optimizely.onReady().then(() => {
        this.props.setIsReady(true)
      })
    },
  }),
  branch(({ isReady }) => isReady === false, renderComponent(CenteredSpinner)),
)

const Application: React.FC<Props> = ({ appId, backend, environment, theme }) => (
  <EnvironmentProvider env={environment}>
    <ThemeProvider theme={theme}>
      <ErrorBoundary>
        <ApolloErrorHandlingProvider>
          <ApolloErrorModalProvider>
            <ApolloProvider>
              <BackendProvider backend={backend} appId={appId}>
                <AnalyticsClientProvider
                  rudderStack={{
                    dataPlaneUrl: environment.get('rudderStackDataPlaneUrl'),
                    writeKey: environment.get('rudderStackWriteKey'),
                  }}
                >
                  <RemoteStringsProvider>
                    <PromptPortalProvider>
                      <PromptProvider>
                        <Router />
                      </PromptProvider>
                    </PromptPortalProvider>
                  </RemoteStringsProvider>
                </AnalyticsClientProvider>
              </BackendProvider>
            </ApolloProvider>
          </ApolloErrorModalProvider>
        </ApolloErrorHandlingProvider>
      </ErrorBoundary>
    </ThemeProvider>
  </EnvironmentProvider>
)

export default enhance(Application)
